import React, { useEffect, useState } from 'react'
import {
  S_GeneralNotes,
  S_ScheduleDetailsPage,
  S_VerificationDetails,
  S_VerificationNotesContainer,
} from './ScheduleDetails.styles'
import {
  approveInvoiceSchedule,
  createInvoiceScheduleGeneralNote,
  getAllInvoiceCount,
  getInvoiceScheduleVerificationDetails,
  getScheduleNotifiableDebtors,
  notifyScheduleDebtors,
  unprocessSchedule,
  updateInvoiceSchedule,
} from '../../../../_services/lender.service'

import { DetailDisplay } from './DetailDisplay'
import { ErrorGroup } from '../../../../_components/ErrorGroup'
import { H3 } from '../../../../_components/Typography/Components/Headings/H3'
import { InvoiceScheduleESigning } from '../InvoiceSchedule/RequestSignatures/RequestSignatures'
import { LoaderButton } from '../../../../_components/LoaderButton'
import { NoteListEditor } from '../../../../_components/Notes/NoteListEditor'
import { NotifyDebtors } from '../../../../_components/modals/notify_debtors/NotifyDebtors'
import { RenderIf } from '../../../../components/helpers/RenderIf/RenderIf'
import { RequestSignaturesProcess } from '../InvoiceSchedule/RequestSignatures/RequestSignaturesProcess/RequestSignaturesProcess'
import { S_Title } from '../../../../_components/Typography/Titles.styles'
import { ScheduleFiles } from '../InvoiceSchedule/ScheduleFiles/ScheduleFiles'
import { SchedulePurchaseControls } from './SchedulePurchaseControls'
import { ScheduleSummary } from './ScheduleSummary'
import { SelectTerms } from '../../lender/InvoiceSchedule/SelectTerms/SelectTerms'
import { SelectedTerms } from '../../lender/InvoiceSchedule/SelectedTerms/SelectedTerms'
import { Text } from '../../../../_components/Typography/Components/Text/Text'
import { VerificationDetails } from './VerificationDetails'
import { WarningModal } from '../../../../_components/modals/Warning/WarningModal'
import { isFeatureEnabled } from '../../../../_helpers/flags'
import { logError } from '../../../../_helpers/errors'
import { unstable_batchedUpdates } from 'react-dom'
import { useFlags } from '../../../../_reduxHooks/flags.hook'
import { useLocks } from '../../../../_reduxHooks/locks.hook'
import { useModal } from '../../../../_reduxHooks/modal.hook'
import { useNotifications } from '../../../../_reduxHooks/notifications.hook'

const canApprove = (business, negativeInvoiceCount, unverifiedInvoiceCount) => {
  if (!business || isNaN(negativeInvoiceCount) || isNaN(unverifiedInvoiceCount)) {
    return false
  }
  if (business.negative_purchase_prohibited && negativeInvoiceCount) {
    return false
  }
  if (business.unverified_purchase_prohibited && unverifiedInvoiceCount) {
    return false
  }
  return true
}

const ScheduleDetails = ({ invoiceSchedule, business, reloadSchedulePage }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isUnprocessing, setIsUnprocessing] = useState(false)
  const [loadingSchedule] = useState(false)
  const [noNegative, setNoNegative] = useState()
  const [noUnverified, setNoUnverified] = useState()
  const [schedule, setSchedule] = useState(invoiceSchedule)
  const [loadingDebtors, setLoadingDebtors] = useState(false)
  const [purchasing, setPurchasing] = useState(false)

  // Note management
  const [newNote, setNewNote] = useState()
  const [noteError, setNoteError] = useState()
  const [isSavingNote, setIsSavingNote] = useState(false)

  // Verification Details
  const [verificationDetails, setVerificationDetails] = useState()

  // Sidebar Files
  const { closeModal, openModal, modal } = useModal()
  const { featureFlags } = useFlags()
  const { showNotification } = useNotifications()

  const { locks } = useLocks()

  const locked = () => {
    let isLocked = Array.isArray(locks['InvoiceSchedule']) && locks['InvoiceSchedule'].includes(Number(schedule.id))
    return isLocked
  }

  const getCounts = async () => {
    const negativeSearchParams = new URLSearchParams()
    negativeSearchParams.set('invoice_schedule_id', schedule.id)
    negativeSearchParams.set('sentiment', 'negative')

    const unverifiedSearchParams = new URLSearchParams()
    unverifiedSearchParams.set('invoice_schedule_id', schedule.id)
    unverifiedSearchParams.set('sentiment', 'non_verified')

    const [negativeCount, unverifiedCount] = await Promise.all([
      getAllInvoiceCount(negativeSearchParams.toString()),
      getAllInvoiceCount(unverifiedSearchParams.toString()),
    ])
    setNoNegative(negativeCount.count)
    setNoUnverified(unverifiedCount.count)
  }

  useEffect(() => {
    setSchedule(invoiceSchedule)
  }, [invoiceSchedule])

  const loadVerificationDetails = () => {
    if (!schedule) return

    getInvoiceScheduleVerificationDetails(schedule.business_id, schedule.id).then((verificationResp) => {
      setVerificationDetails(verificationResp)
    })
  }

  useEffect(() => {
    if (schedule === undefined && !loadingSchedule && !locked()) {
      reloadSchedulePage()
    }
  }, [schedule, locked()])

  useEffect(() => {
    if (!modal.is_open && !isUnprocessing) {
      setIsUnprocessing(false)
    }
  }, [modal.is_open])

  useEffect(() => {
    if (!schedule) return

    loadVerificationDetails()
    getCounts()
  }, [schedule])

  // Refresh notes after saving
  useEffect(() => {
    if (isSavingNote || schedule == undefined) return

    reloadSchedulePage()
  }, [isSavingNote])

  useEffect(() => {
    if (locked() && !purchasing) {
      openModal(WarningModal, {
        title: 'Schedule Purchased',
        body: 'This invoice schedule is being purchased.',
        onDecline: () => {
          closeModal()
        },
        declineLabel: 'Okay',
        showAccept: false,
      })
    } else {
      reloadSchedulePage()
    }
  }, [locked()])

  const handleNoteChange = (val) => {
    setNoteError('')
    setNewNote(val)
  }

  const handleNoteSubmit = () => {
    if (newNote === '') {
      setNoteError('Empty note')
      return
    }

    setIsSavingNote(true)
    createInvoiceScheduleGeneralNote(schedule.id, newNote).then(
      () => {
        unstable_batchedUpdates(() => {
          setIsSavingNote(false)
          setNewNote('')
        })
      },
      (e) => {
        unstable_batchedUpdates(() => {
          setIsSavingNote(false)
          setNoteError('Network error')
          setNewNote('')
        })
        logError(e)
      }
    )
  }

  const getScheduleNotesTitle = (notes) => {
    if (!notes || !notes.length) return 'No Notes'

    const length = notes.length
    const singular = length === 1

    return `${length} ${singular ? 'Note' : 'Notes'}`
  }

  const renderUnprocessButton = () => {
    if (
      isFeatureEnabled('unprocess_schedule', featureFlags) &&
      schedule &&
      (schedule.state == 'closed' || schedule.state == 'purchased' || schedule.state == 'funded')
    ) {
      return (
        <LoaderButton
          className="destroy unprocess"
          isLoading={isUnprocessing}
          onClick={() => {
            setIsUnprocessing(true)
            openModal(WarningModal, {
              title: 'Warning',
              body:
                'Do you wish to unprocess this Invoice Schedule?' +
                ' Doing so will remove the invoices as purchased ' +
                'and place the Invoice Schedule back into the Pending state.',
              onAccept: () => {
                unprocessSchedule(schedule.id, schedule.business_id)
                  .then(() => {
                    reloadSchedulePage()
                  })
                  .catch((e) => {
                    openModal(WarningModal, {
                      title: 'Error',
                      body: e,
                      declineLabel: 'Ok',
                      showAccept: false,
                    })
                  })
                  .finally(() => setIsUnprocessing(false))
              },
              onDecline: () => setIsUnprocessing(false),
            })
          }}
        >
          Unprocess
        </LoaderButton>
      )
    } else {
      return null
    }
  }

  const renderNotifyDebtorsButton = () => {
    if (schedule && (schedule.state == 'closed' || schedule.state == 'purchased' || schedule == 'funded')) {
      return (
        <LoaderButton
          isLoading={loadingDebtors}
          onClick={() => {
            setLoadingDebtors(true)
            getScheduleNotifiableDebtors(schedule.id).then((debtors) => {
              openModal(NotifyDebtors, {
                debtors: debtors,
                btnText: 'Notify',
                invoiceScheduleId: schedule.id,
                onSelectionFinished: async (debtors) => {
                  await notifyScheduleDebtors(schedule.id, debtors)
                  closeModal()
                  showNotification({
                    type: `success`,
                    message: 'Notifications sent.',
                  })
                },
              })
              setLoadingDebtors(false)
            })
          }}
        >
          Notify Debtors
        </LoaderButton>
      )
    } else {
      return null
    }
  }

  const ApproveInvoiceModal = ({ id, setSchedule }) => {
    const [isApproving, setIsApproving] = useState(false)
    return (
      <WarningModal
        acceptClassName={'approve'}
        acceptLabel={'Approve'}
        body={() => {
          return (
            <>
              <Text align={'center'}>Are you sure you want to approve this schedule?</Text>
              <Text align={'center'}>Some document signatures are still missing.</Text>
            </>
          )
        }}
        declineLabel={'Cancel'}
        isLoading={isApproving}
        onAccept={async () => {
          setIsApproving(true)
          approveInvoiceSchedule(id)
            .then((resp) => {
              setSchedule(resp)
            })
            .catch((e) => {
              openModal(ErrorGroup, {
                errors: e.data.errors,
                title: 'Invoice Errors',
              })
            })
            .finally(() => setIsApproving(false))
        }}
        onDecline={() => {
          setIsApproving(false)
        }}
        title={'Confirm'}
      />
    )
  }

  const [createdDocs, setCreatedDocs] = useState([])

  return (
    <S_ScheduleDetailsPage>
      <>
        <S_Title>Invoice Schedule #{invoiceSchedule.batch_number}</S_Title>
        <header className={`header`}>
          {schedule && !loadingSchedule && <ScheduleSummary business={business} schedule={schedule} />}
          <div>
            {schedule && (
              <SchedulePurchaseControls
                business={business}
                closeModal={closeModal}
                featureFlags={featureFlags}
                isModalOpen={modal.is_open}
                locked={locked()}
                openModal={openModal}
                purchasePassThrough={(purch) => {
                  if (purch) {
                    setPurchasing(true)
                  }
                }}
                reload={reloadSchedulePage}
                renderNotifyDebtorsButton={renderNotifyDebtorsButton}
                renderUnprocessButton={renderUnprocessButton}
                schedule={schedule}
                /*reload schedule after purchase*/
                updateSchedule={(schedule = undefined) => unstable_batchedUpdates(() => setSchedule(schedule))}
              />
            )}
            {schedule && schedule.state == 'pending' && (
              <LoaderButton
                className={`secondary`}
                isLoading={isLoading}
                onClick={() => {
                  setIsLoading(true)
                  updateInvoiceSchedule(schedule.id, null).then((resp) => {
                    // Massage notes
                    resp.notes = resp.notes.map((note) => ({
                      ...note,
                      note: note.body,
                    }))
                    setIsLoading(false)
                    reloadSchedulePage()
                  })
                }}
              >
                Back To Input
              </LoaderButton>
            )}
            {schedule && schedule.state == 'pending' && (
              <LoaderButton
                className={`approve-btn`}
                disabled={!canApprove(business, noNegative, noUnverified)}
                isLoading={isLoading}
                onClick={() => {
                  setIsLoading(true)
                  if (schedule.has_pending_signature_request) {
                    openModal(ApproveInvoiceModal, { id: schedule.id, setSchedule: setSchedule })
                  } else {
                    approveInvoiceSchedule(schedule.id)
                      .then((resp) => {
                        setSchedule(resp)
                      })
                      .catch((e) => {
                        openModal(ErrorGroup, {
                          errors: e.data.errors,
                          title: 'Invoice Errors',
                        })
                      })
                      .finally(() => setIsLoading(false))
                  }
                  setIsLoading(false)
                }}
              >
                Approve
              </LoaderButton>
            )}
          </div>
        </header>
        <S_VerificationNotesContainer>
          <S_GeneralNotes>
            <H3 fw={400} mb={'10px'}>
              Invoice Schedule Notes
            </H3>
            <NoteListEditor
              error={noteError}
              flexType={'column'}
              isSaving={isSavingNote}
              label={getScheduleNotesTitle(schedule.notes)}
              newNoteValue={newNote}
              noteStyle={'businessNotes'}
              notes={schedule.notes}
              onChange={handleNoteChange}
              onSave={handleNoteSubmit}
            />
          </S_GeneralNotes>
          {verificationDetails && (
            <S_VerificationDetails>
              <VerificationDetails details={verificationDetails} />
            </S_VerificationDetails>
          )}
        </S_VerificationNotesContainer>
        <DetailDisplay
          closeModal={closeModal}
          featureFlags={featureFlags}
          loadingSchedule={loadingSchedule}
          onAddVerificationNote={() => {
            reloadSchedulePage()
          }}
          openModal={openModal}
          schedule={schedule}
          scheduleId={schedule.id}
          setSchedule={setSchedule}
        />
      </>

      {schedule && schedule.state == 'pending' && <SelectTerms business={business} invoiceSchedule={schedule} />}

      {schedule && (schedule.state == 'approved' || schedule.state == 'purchased') && (
        <SelectedTerms invoiceSchedule={schedule} />
      )}

      <RenderIf condition={schedule}>
        <InvoiceScheduleESigning createdDocs={createdDocs} schedule={schedule} setCreatedDocs={setCreatedDocs} />

        <RequestSignaturesProcess createdDocs={createdDocs} schedule={schedule} />
      </RenderIf>

      <ScheduleFiles invoiceSchedule={schedule} />
    </S_ScheduleDetailsPage>
  )
}

export { ScheduleDetails }
